/**
 * JSON Extras Renderers
 * Renders supporting data files (translations, types, tags, variables) as JSON
 */

import type { StringsItem } from '../../../stores/stringsStore';
import { stripHtmlTags } from '../../core/formatting';

/**
 * Render translations as a JSON string
 * Produces { translations: { [key]: { source_text, abbrev, translations: { lang: text } } } }
 *
 * @param stringsItems - Array of string items with translations
 * @param gameLanguages - Array of language codes configured for the game
 */
export function renderJsonTranslationsFile(
  stringsItems: StringsItem[],
  gameLanguages?: string[]
): string {
  if (!gameLanguages || gameLanguages.length === 0) {
    const languagesSet = new Set<string>();
    languagesSet.add('en');
    stringsItems.forEach(item => {
      if (item.translations && Array.isArray(item.translations)) {
        item.translations.forEach(trans => {
          if (trans.language_code) languagesSet.add(trans.language_code);
        });
      }
    });
    gameLanguages = Array.from(languagesSet).sort();
  }

  const translations: Record<string, any> = {};

  stringsItems.forEach(item => {
    const key = item.abbrev || item.id;
    const entry: Record<string, any> = {
      id: item.id,
      source_text: stripHtmlTags(item.source_text || ''),
      section: item.section || null,
      context: item.context || null,
      translations: {} as Record<string, string>
    };

    if (item.translations && Array.isArray(item.translations)) {
      item.translations.forEach(trans => {
        if (trans.language_code && trans.text) {
          entry.translations[trans.language_code] = stripHtmlTags(trans.text);
        }
      });
    }

    translations[key] = entry;
  });

  console.log(`✅ Rendered ${stringsItems.length} translation strings as JSON`);

  return JSON.stringify({
    languages: gameLanguages,
    translations
  }, null, 2);
}

/**
 * Render types as a JSON string
 * Produces { types: { [groupName]: { elements: [ { id, name, code, value } ] } } }
 *
 * @param types - Array of type groups with data
 */
export function renderJsonTypesFile(types: any[]): string {
  const typesOutput: Record<string, any> = {};

  types.forEach((typeGroup, groupIndex) => {
    if (!typeGroup.data || !Array.isArray(typeGroup.data) || typeGroup.data.length === 0) {
      return;
    }

    const groupName = typeGroup.name || `Group${groupIndex}`;
    typesOutput[groupName] = {
      id: typeGroup.id || null,
      elements: typeGroup.data.map((element: any) => ({
        id: element.id || '',
        name: element.name || '',
        code: element.code || element.name || '',
        value: element.value || ''
      }))
    };
  });

  console.log(`✅ Rendered ${types.length} type groups as JSON`);

  return JSON.stringify({ types: typesOutput }, null, 2);
}

/**
 * Render tags as a JSON string
 * Produces { tags: { [groupName]: { elements: [ { id, name, code, value } ] } } }
 *
 * @param tags - Array of tag groups with data
 */
export function renderJsonTagsFile(tags: any[]): string {
  const tagsOutput: Record<string, any> = {};

  tags.forEach((tagGroup, groupIndex) => {
    if (!tagGroup.data || !Array.isArray(tagGroup.data) || tagGroup.data.length === 0) {
      return;
    }

    const groupName = tagGroup.name || `Group${groupIndex}`;
    tagsOutput[groupName] = {
      id: tagGroup.id || null,
      elements: tagGroup.data.map((element: any) => ({
        id: element.id || '',
        name: element.name || '',
        code: element.code || element.name || '',
        value: element.value || ''
      }))
    };
  });

  console.log(`✅ Rendered ${tags.length} tag groups as JSON`);

  return JSON.stringify({ tags: tagsOutput }, null, 2);
}

/**
 * Render variables as a JSON string
 * Produces { variables: { [groupName]: { elements: [ { id, name, type, value, ... } ] } } }
 *
 * @param variables - Array of variable groups (stored as types in the backend)
 */
export function renderJsonVariablesFile(variables: any[]): string {
  const variablesOutput: Record<string, any> = {};

  variables.forEach((varGroup, groupIndex) => {
    if (!varGroup.data || !Array.isArray(varGroup.data) || varGroup.data.length === 0) {
      return;
    }

    const groupName = varGroup.name || `Group${groupIndex}`;
    variablesOutput[groupName] = {
      id: varGroup.id || null,
      elements: varGroup.data.map((element: any) => {
        const entry: Record<string, any> = {
          id: element.id || '',
          name: element.name || '',
          code: element.code || element.name || ''
        };

        // Include type-specific values
        if (element.value !== undefined) entry.value = element.value;
        if (element.numericValue !== undefined) entry.numeric_value = element.numericValue;
        if (element.percentageValue !== undefined) entry.percentage_value = element.percentageValue;
        if (element.minValue !== undefined) entry.min_value = element.minValue;
        if (element.maxValue !== undefined) entry.max_value = element.maxValue;
        if (element.increment !== undefined) entry.increment = element.increment;
        if (element.linkedValue !== undefined) entry.linked_value = element.linkedValue;
        if (element.description !== undefined) entry.description = element.description;

        return entry;
      })
    };
  });

  console.log(`✅ Rendered ${variables.length} variable groups as JSON`);

  return JSON.stringify({ variables: variablesOutput }, null, 2);
}

/**
 * Render dialog variables as a JSON string
 * Produces { dialog_variables: { [dialogKey]: { variables: [ { name, type, initial_value } ] } } }
 *
 * @param dialogs - Array of dialog objects with variable definitions
 */
export function renderJsonDialogVariablesFile(dialogs: any[]): string {
  const output: Record<string, any> = {};

  if (dialogs && dialogs.length > 0) {
    dialogs.forEach(dialog => {
      const dialogKey = dialog.dialog_key || dialog.name || 'unknown';
      const variables = dialog.data?.variables;

      if (variables && Array.isArray(variables) && variables.length > 0) {
        output[dialogKey] = {
          dialog_name: dialog.name || '',
          variables: variables.map((v: any) => ({
            name: v.name,
            type: v.type || 'boolean',
            initial_value: v.initialValue ?? (v.type === 'number' ? 0 : false),
            description: v.description || null
          }))
        };
      }
    });
  }

  console.log(`✅ Rendered dialog variables for ${Object.keys(output).length} dialogs as JSON`);

  return JSON.stringify({ dialog_variables: output }, null, 2);
}
